// Floating text popups for score, level-ups, and pickups.
// Gate 1: simple canvas-text sprites, no external assets.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';
import { easeOutCubic } from './easing.js';

const MAX_TEXTS = 32;
const LIFETIME = 0.9;
const RISE_SPEED = 1.8;

interface ActiveText {
  sprite: THREE.Sprite;
  life: number;
  vx: number;
  vy: number;
}

export class FloatingText {
  private readonly active: ActiveText[] = [];

  constructor(private readonly scene: THREE.Scene) {}

  /**
   * Spawn a popup. `subtle` is for high-frequency pops (mote collects): no
   * accent border, a much dimmer backing, no glow — present, not loud.
   * `worldScale` shrinks the world-space sprite for big recurring reads
   * (LUMENSURGE): a full-size plate re-spawning every wave-clear vac-train
   * out-ranks the player in the visual hierarchy.
   */
  spawn(pos: Vec2, text: string, color: string, size = 28, subtle = false, worldScale = 1): void {
    if (this.active.length >= MAX_TEXTS) {
      this.remove(0);
    }

    // Floor the canvas font size: Silkscreen glyphs below ~20 px smear into
    // unreadable blocks once the sprite shrinks to world size. Subtle pops
    // shrink the SPRITE instead (see worldHeight below), keeping the font.
    const px = Math.max(size, 20);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.font = `bold ${px}px Silkscreen, monospace`;
    const metrics = ctx.measureText(text);
    const padX = 14;
    const padY = 8;
    canvas.width = Math.ceil(metrics.width) + padX * 2;
    canvas.height = Math.ceil(px * 1.4) + padY * 2;
    const w = canvas.width;
    const h = canvas.height;

    // Dark semi-transparent backing chip with an accent-tinted edge, drawn
    // into the canvas behind the text: the popup reads over any swarm
    // brightness (the old glow-only text read as a blank white rounded chip).
    ctx.beginPath();
    ctx.roundRect(1.5, 1.5, w - 3, h - 3, 7);
    ctx.fillStyle = subtle ? 'rgba(3, 8, 18, 0.55)' : 'rgba(3, 8, 18, 0.78)';
    ctx.fill();
    if (!subtle) {
      ctx.globalAlpha = 0.45;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    ctx.font = `bold ${px}px Silkscreen, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    if (!subtle) {
      ctx.shadowColor = color;
      ctx.shadowBlur = 6;
    }
    ctx.fillText(text, w / 2, h / 2 + 1);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      opacity: 1,
    });

    const sprite = new THREE.Sprite(material);
    // Subtle pops render smaller in world units (the font stays ≥20px sharp).
    // Non-subtle chips render ~1.5x larger: at 1.05u the Silkscreen glyphs
    // fell below their legibility floor at desktop zoom and smeared.
    const worldHeight = (subtle ? 0.9 : 1.575) * worldScale;
    const worldWidth = worldHeight * (canvas.width / canvas.height);
    sprite.scale.set(worldWidth, worldHeight, 1);
    sprite.position.set(pos.x, pos.y + 0.4, 0.5);
    sprite.userData = { material, lastScale: 1 };

    this.scene.add(sprite);

    const angle = (Math.random() - 0.5) * 0.5;
    const speed = RISE_SPEED * (0.85 + Math.random() * 0.3);
    this.active.push({
      sprite,
      life: LIFETIME,
      vx: Math.sin(angle) * 0.5,
      vy: Math.cos(angle) * speed,
    });
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const n = this.active[i];
      n.life -= dt;
      if (n.life <= 0) {
        this.remove(i);
        continue;
      }
      n.sprite.position.x += n.vx * dt;
      n.sprite.position.y += n.vy * dt;
      const t = n.life / LIFETIME;
      n.sprite.material.opacity = easeOutCubic(Math.min(1, t * 1.6));
      const scale = 1 + easeOutCubic(1 - t) * 0.2;
      n.sprite.scale.x *= scale / (n.sprite.userData.lastScale ?? 1);
      n.sprite.scale.y *= scale / (n.sprite.userData.lastScale ?? 1);
      n.sprite.userData.lastScale = scale;
    }
  }

  dispose(): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      this.remove(i);
    }
  }

  private remove(i: number): void {
    const n = this.active[i];
    this.scene.remove(n.sprite);
    n.sprite.material.map?.dispose();
    n.sprite.material.dispose();
    this.active.splice(i, 1);
  }
}
